import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { negotiatePubSub } from '../apis/pubsubNegotiate';

const WebPubSubContext = createContext(null);

const SOCKET_IO_HUB_PATH = '/clients/socketio/hubs/notifications';
const CONTENT_EXTRACTION_STATUS = 'content_extraction_status';

function parseMessageData(data) {
    if (data == null) return null;

    let payload = data;
    if (Array.isArray(payload) && payload.length === 1) {
        payload = payload[0];
    }

    if (payload instanceof ArrayBuffer) {
        payload = new TextDecoder().decode(payload);
    } else if (ArrayBuffer.isView(payload)) {
        payload = new TextDecoder().decode(payload);
    }

    if (typeof payload === 'string') {
        try {
            return JSON.parse(payload);
        } catch {
            return null;
        }
    }

    if (typeof payload === 'object') {
        return payload;
    }

    return null;
}

/** Normalize negotiate response into Socket.IO connection options. */
export function parseNegotiateResponse(data) {
    if (!data) {
        throw new Error('Empty pubsub negotiate response');
    }

    // Azure Socket.IO negotiate shape: { endpoint, path, token }
    if (data.endpoint && (data.token || data.access_token)) {
        return {
            endpoint: String(data.endpoint).replace(/\/$/, ''),
            path: data.path || SOCKET_IO_HUB_PATH,
            accessToken: data.token || data.access_token,
        };
    }

    // Backend shape: { url } — may already include path + access_token
    if (data.url) {
        const parsed = new URL(data.url);
        const accessToken = parsed.searchParams.get('access_token');
        const path =
            parsed.pathname && parsed.pathname !== '/'
                ? parsed.pathname.replace(/\/$/, '')
                : SOCKET_IO_HUB_PATH;

        return {
            endpoint: parsed.origin,
            path,
            accessToken,
        };
    }

    throw new Error('Invalid pubsub negotiate response: expected url or endpoint+token');
}

/** Map server payload to the camelCase shape existing handlers expect. */
export function mapContentExtractionStatus(payload, { requireEventType = true } = {}) {
    if (!payload) {
        return null;
    }

    if (requireEventType && payload.event_type !== CONTENT_EXTRACTION_STATUS) {
        return null;
    }

    const jobId = payload.job_id ?? payload.scorm_file_id;
    if (jobId == null) {
        return null;
    }

    return {
        jobId,
        status: payload.status,
        download_url: payload.download_url ?? null,
        orgId: payload.organization_id ?? payload.org_id ?? payload.orgId,
    };
}

export const useWebPubSub = () => {
    const context = useContext(WebPubSubContext);
    if (!context) {
        throw new Error('useWebPubSub must be used within WebPubSubProvider');
    }
    return context;
};

/**
 * Starts a single Socket.IO client (Azure Web PubSub for Socket.IO) for the authenticated app shell.
 * Re-negotiates access token on reconnect (~60 min). Callers register status listeners via subscribe.
 */
export function WebPubSubProvider({ children }) {
    const listenersRef = useRef(new Set());
    const socketRef = useRef(null);

    const subscribe = useCallback((listener) => {
        if (typeof listener !== 'function') {
            return () => {};
        }
        listenersRef.current.add(listener);
        return () => {
            listenersRef.current.delete(listener);
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        let socket = null;

        const handleContentExtractionStatus = (data) => {
            const payload = parseMessageData(data);
            const jobStatus = mapContentExtractionStatus(payload, { requireEventType: false });
            if (!jobStatus) return;

            listenersRef.current.forEach((listener) => {
                try {
                    listener(jobStatus);
                } catch (err) {
                    console.error('PubSub listener error:', err);
                }
            });
        };

        const connect = async () => {
            try {
                const negotiated = await negotiatePubSub();
                if (cancelled) return;

                const { endpoint, path, accessToken } = parseNegotiateResponse(negotiated);
                if (!accessToken) {
                    throw new Error('Negotiate response missing access_token');
                }

                socket = io(endpoint, {
                    path,
                    transports: ['websocket'],
                    query: { access_token: accessToken },
                    reconnection: true,
                });
                socketRef.current = socket;

                socket.on('connect', () => {
                    console.log('Socket.IO connected', socket.id);
                });

                socket.on(CONTENT_EXTRACTION_STATUS, handleContentExtractionStatus);

                // Re-negotiate before Engine.IO reconnects (token ~60 min)
                socket.io.on('reconnect_attempt', async () => {
                    try {
                        const next = await negotiatePubSub();
                        const parsed = parseNegotiateResponse(next);
                        socket.io.opts.query = { access_token: parsed.accessToken };
                    } catch (err) {
                        console.error('Failed to re-negotiate Socket.IO token:', err);
                    }
                });

                socket.on('connect_error', (err) => {
                    console.error('Socket.IO connect error:', err?.message || err);
                });
            } catch (err) {
                if (!cancelled) {
                    console.error('Failed to start Socket.IO client:', err);
                }
            }
        };

        connect();

        return () => {
            cancelled = true;
            if (socket) {
                socket.off(CONTENT_EXTRACTION_STATUS, handleContentExtractionStatus);
                socket.disconnect();
            }
            if (socketRef.current === socket) {
                socketRef.current = null;
            }
        };
    }, []);

    return <WebPubSubContext.Provider value={{ subscribe }}>{children}</WebPubSubContext.Provider>;
}
